/**
 * Postgres vs Elasticsearch on the same question.
 *
 * This panel exists because on 2026-07-30 the two stores disagreed by 5,980,100
 * documents on the in-window count and 12,779,854 on the fresh count, and
 * nothing anywhere surfaced it. A dashboard reading one store cannot tell you the
 * other has rotted; publishing the gap turns replica lag into a number.
 *
 * Postgres is the system of record, so a non-trivial delta means ES is wrong —
 * it is not a tie to be split.
 */
import { useDivergence } from '../api/queries'
import { PanelState, Pill, Tile } from '../components/ui'
import { age, n } from '../components/format'

const ROWS = ['in_window', 'fresh_7d', 'fresh_30d', 'stale_30d'] as const

export function Divergence() {
  const { data, isLoading, error } = useDivergence()

  if (data && data.configured === false) {
    return <Tile title="PG ↔ ES divergence"><p className="panel-load">ES not configured</p></Tile>
  }
  const v = data && 'videos_90d' in data ? data.videos_90d : undefined
  const err = v && 'error' in v ? v.error : null
  const es = v && 'elasticsearch' in v ? v.elasticsearch : undefined
  const pg = v && 'postgres' in v ? v.postgres : undefined
  const delta = v && 'delta' in v ? v.delta : undefined
  const agrees = v && 'agrees' in v ? v.agrees : undefined

  return (
    <Tile title="PG ↔ ES divergence" hint="videos, 90-day window">
      <PanelState isLoading={isLoading} error={error}>
        {err ? <p className="panel-err">{err}</p> : (
          <>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              {agrees === undefined
                ? <Pill state="idle">no snapshot to compare</Pill>
                : agrees
                  ? <Pill state="ok">stores agree</Pill>
                  : <Pill state="crit">replica has drifted</Pill>}
              {data && 'postgres_snapshot_age_s' in data && data.postgres_snapshot_age_s != null
                ? <span className="mono" style={{ fontSize: 11, color: 'var(--dim2)' }}>
                    PG snapshot {age(data.postgres_snapshot_age_s)} old
                  </span>
                : null}
            </div>
            <table className="t">
              <thead>
                <tr><th>metric</th><th className="n">Postgres</th><th className="n">ES</th><th className="n">delta</th></tr>
              </thead>
              <tbody>
                {ROWS.map((k) => {
                  const d = delta?.[k]
                  return (
                    <tr key={k}>
                      <td>{k}</td>
                      <td className="n">{n(pg?.[k] ?? null)}</td>
                      <td className="n q">{n(es?.[k] ?? null)}</td>
                      <td className="n" style={d ? { color: 'var(--crisis)' } : undefined}>
                        {d === undefined ? '—' : `${d > 0 ? '+' : ''}${n(d)}`}
                      </td>
                    </tr>
                  )
                })}
                {es?.['duplicate_estimate'] !== undefined ? (
                  <tr>
                    <td className="q">duplicate docs in ES (est.)</td>
                    <td className="n q">—</td>
                    <td className="n" style={{ color: 'var(--warning)' }}>{n(es['duplicate_estimate'])}</td>
                    <td />
                  </tr>
                ) : null}
              </tbody>
            </table>
            <p className="note">
              Postgres is the system of record, but its figures here are a page sample: totals
              swing several percent run to run, so the verdict compares freshness ratios, not
              these deltas.
            </p>
          </>
        )}
      </PanelState>
    </Tile>
  )
}
